import React, { useMemo } from "react";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { getSalaryRanges, getRegionForCountry } from "@/utils/salaryRanges";
import { FormSectionProps } from "./types";

const WorkPreferencesSection: React.FC<FormSectionProps> = ({
  setValue,
  watchedFields,
  errors,
}) => {
  const workSetup = watchedFields.workSetup;
  const workEnvironment = watchedFields.workEnvironment;
  const selectedCountry = watchedFields.country;
  const nextRoleLevel = watchedFields.nextRoleLevel;

  // Get salary ranges based on selected country and role level
  const salaryRanges = useMemo(() => {
    // Default to 'non-european' region if no country selected
    const region = selectedCountry ? getRegionForCountry(selectedCountry) : 'non-european';
    
    // Default to 'mid' role level if no role level selected
    const roleLevel = (nextRoleLevel || 'mid') as 'junior' | 'mid' | 'senior' | 'staff' | 'principal';
    
    return getSalaryRanges(region, roleLevel);
  }, [selectedCountry, nextRoleLevel]);

  const handleWorkSetupChange = (value: "remote" | "hybrid" | "in-office", checked: boolean) => {
    const current = workSetup || [];
    if (checked) {
      setValue("workSetup", [...current, value]);
    } else {
      setValue("workSetup", current.filter((item) => item !== value));
    }
  };

  const handleWorkEnvironmentChange = (
    value: "solo" | "small-teams" | "medium-teams" | "large-teams" | "startups" | "big-companies",
    checked: boolean
  ) => {
    const current = workEnvironment || [];
    if (checked) {
      setValue("workEnvironment", [...current, value]);
    } else {
      setValue("workEnvironment", current.filter((item) => item !== value));
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-lg">Work Preferences</h3>
      <div className="space-y-4">
        <div className="space-y-2" data-question-field="nextRoleLevel">
          <Label>
            What role level are you aiming next? <span className="text-red-500">*</span>
          </Label>
          <RadioGroup
            value={watchedFields.nextRoleLevel}
            onValueChange={(value) =>
              setValue("nextRoleLevel", value as "mid" | "senior" | "staff" | "principal")
            }
            className="space-y-1 text-neutral-500"
          >
            <div className="flex items-center space-x-4">
              <RadioGroupItem value="mid" id="next-mid" />
              <Label htmlFor="next-mid" className="font-normal cursor-pointer">
                Mid
              </Label>
            </div>
            <div className="flex items-center space-x-4">
              <RadioGroupItem value="senior" id="next-senior" />
              <Label htmlFor="next-senior" className="font-normal cursor-pointer">
                Senior
              </Label>
            </div>
            <div className="flex items-center space-x-4">
              <RadioGroupItem value="staff" id="next-staff" />
              <Label htmlFor="next-staff" className="font-normal cursor-pointer">
                Staff
              </Label>
            </div>
            <div className="flex items-center space-x-4">
              <RadioGroupItem value="principal" id="next-principal" />
              <Label htmlFor="next-principal" className="font-normal cursor-pointer">
                Principal
              </Label>
            </div>
          </RadioGroup>
          {errors.nextRoleLevel && (
            <p className="text-sm text-red-500">{errors.nextRoleLevel.message}</p>
          )}
        </div>

        <div className="space-y-2" data-question-field="workSetup">
          <Label>
            What work setup do you prefer? <span className="text-red-500">*</span>
          </Label>
          <div className="space-y-3">
            <div className="flex text-neutral-500 items-center space-x-4">
              <Checkbox
                id="remote"
                checked={workSetup?.includes("remote")}
                onCheckedChange={(checked) =>
                  handleWorkSetupChange("remote", checked as boolean)
                }
              />
              <Label htmlFor="remote" className="font-normal cursor-pointer">
                Remote
              </Label>
            </div>
            <div className="flex items-center text-neutral-500 space-x-4">
              <Checkbox
                id="hybrid"
                checked={workSetup?.includes("hybrid")}
                onCheckedChange={(checked) =>
                  handleWorkSetupChange("hybrid", checked as boolean)
                }
              />
              <Label htmlFor="hybrid" className="font-normal cursor-pointer">
                Hybrid
              </Label>
            </div>
            <div className="flex text-neutral-500 items-center space-x-4">
              <Checkbox
                id="in-office"
                checked={workSetup?.includes("in-office")}
                onCheckedChange={(checked) =>
                  handleWorkSetupChange("in-office", checked as boolean)
                }
              />
              <Label htmlFor="in-office" className="font-normal cursor-pointer">
                In office
              </Label>
            </div>
          </div>
          {errors.workSetup && (
            <p className="text-sm text-red-500">{errors.workSetup.message}</p>
          )}
        </div>

        <div className="space-y-2" data-question-field="workEnvironment">
          <Label>
            Which type of environment do you prefer working in?{" "}
            <span className="text-red-500">*</span>
          </Label>
          <div className="space-y-3">
            <div className="flex items-center text-neutral-500 space-x-4">
              <Checkbox
                id="env-solo"
                checked={workEnvironment?.includes("solo")}
                onCheckedChange={(checked) =>
                  handleWorkEnvironmentChange("solo", checked as boolean)
                }
              />
              <Label htmlFor="env-solo" className="font-normal cursor-pointer">
                Solo projects, full ownership
              </Label>
            </div>
            <div className="flex items-center text-neutral-500 space-x-4">
              <Checkbox
                id="env-small"
                checked={workEnvironment?.includes("small-teams")}
                onCheckedChange={(checked) =>
                  handleWorkEnvironmentChange("small-teams", checked as boolean)
                }
              />
              <Label htmlFor="env-small" className="font-normal cursor-pointer">
                Small teams, close collaboration
              </Label>
            </div>
            <div className="flex items-center text-neutral-500 space-x-4">
              <Checkbox
                id="env-medium"
                checked={workEnvironment?.includes("medium-teams")}
                onCheckedChange={(checked) =>
                  handleWorkEnvironmentChange("medium-teams", checked as boolean)
                }
              />
              <Label htmlFor="env-medium" className="font-normal cursor-pointer">
                Medium teams, structured but flexible
              </Label>
            </div>
            <div className="flex items-center text-neutral-500 space-x-4">
              <Checkbox
                id="env-large"
                checked={workEnvironment?.includes("large-teams")}
                onCheckedChange={(checked) =>
                  handleWorkEnvironmentChange("large-teams", checked as boolean)
                }
              />
              <Label htmlFor="env-large" className="font-normal cursor-pointer">
                Large teams, clear roles and processes
              </Label>
            </div>
            <div className="flex items-center text-neutral-500 space-x-4">
              <Checkbox
                id="env-startups"
                checked={workEnvironment?.includes("startups")}
                onCheckedChange={(checked) =>
                  handleWorkEnvironmentChange("startups", checked as boolean)
                }
              />
              <Label htmlFor="env-startups" className="font-normal cursor-pointer">
                Startups, fast and intense
              </Label>
            </div>
            <div className="flex items-center text-neutral-500 space-x-4">
              <Checkbox
                id="env-big"
                checked={workEnvironment?.includes("big-companies")}
                onCheckedChange={(checked) =>
                  handleWorkEnvironmentChange("big-companies", checked as boolean)
                }
              />
              <Label htmlFor="env-big" className="font-normal cursor-pointer">
                Big companies, stable and well resourced
              </Label>
            </div>
          </div>
          {errors.workEnvironment && (
            <p className="text-sm text-red-500">{errors.workEnvironment.message}</p>
          )}
        </div>

        <div className="space-y-2" data-question-field="expectedSalaryRange">
          <Label>
            What is your expected salary range? <span className="text-red-500">*</span>
          </Label>
          <ToggleGroup
            type="single"
            value={watchedFields.expectedSalaryRange}
            onValueChange={(value) => {
              if (value) {
                setValue("expectedSalaryRange", value);
              }
            }}
            className="justify-start flex-wrap"
          >
            {salaryRanges.map((range) => (
              <ToggleGroupItem
                key={range.value}
                className="border border-input data-[state=on]:bg-black data-[state=on]:text-white"  
                value={range.value}
                id={`salary-${range.value}`}
                aria-label={range.label}
              >
                {range.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
          {errors.expectedSalaryRange && (
            <p className="text-sm text-red-500">{errors.expectedSalaryRange.message}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkPreferencesSection;

